import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { vendorUrlRegexPath, withPromoURL } from 'src/helpers/service';

enum Status {
  loading = 'loading',
  done = 'done',
  fail = 'fail',
}

interface IUnseenCount {
  count: number;
  display: string;
}

interface IUnseenCountsResponse {
  vendorSharedPromotions: IUnseenCount;
  vendorSharedCoupons: IUnseenCount;
}

interface IState {
  status: Status | '';
  vendorSharedPromotions: IUnseenCount;
  vendorSharedCoupons: IUnseenCount;
  error: any;
}

const emptyCount: IUnseenCount = { count: 0, display: '0' };

const initialState: IState = {
  status: '',
  vendorSharedPromotions: { ...emptyCount },
  vendorSharedCoupons: { ...emptyCount },
  error: null,
};

const toDisplay = (count: number): string => {
  if (!count || count <= 0) return '0';
  return count > 99 ? '99+' : `${count}`;
};

export const fetchUnseenSharedOfferCounts = createAsyncThunk('vendor-shared-offers/unseen-counts', async () => {
  const res = await axios.get<IUnseenCountsResponse>(withPromoURL(vendorUrlRegexPath(':vendorId/vendor-shared-offers/unseen-counts')));
  return res.data;
});

const vendorSharedOffersSlice = createSlice({
  name: 'vendor-shared-offers',
  initialState,
  reducers: {
    setVendorSharedPromotionsCount: (state, action: PayloadAction<number>) => {
      const count = action.payload || 0;
      state.vendorSharedPromotions = { count, display: toDisplay(count) };
    },
    setVendorSharedCouponsCount: (state, action: PayloadAction<number>) => {
      const count = action.payload || 0;
      state.vendorSharedCoupons = { count, display: toDisplay(count) };
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<IState>) => {
    builder.addCase(fetchUnseenSharedOfferCounts.pending, (state) => {
      state.status = Status.loading;
    });
    builder.addCase(fetchUnseenSharedOfferCounts.fulfilled, (state, action) => {
      state.status = Status.done;
      state.vendorSharedPromotions = action.payload?.vendorSharedPromotions || { ...emptyCount };
      state.vendorSharedCoupons = action.payload?.vendorSharedCoupons || { ...emptyCount };
    });
    builder.addCase(fetchUnseenSharedOfferCounts.rejected, (state, action) => {
      state.status = Status.fail;
      state.error = action.error;
    });
  },
});

export const { setVendorSharedPromotionsCount, setVendorSharedCouponsCount } = vendorSharedOffersSlice.actions;

export default vendorSharedOffersSlice.reducer;
